import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fontWeights, radii } from '../../../theme/tokens';
import AssemblingProgress from '../../Archive/AssemblingProgress';
import { QrStatus } from '../../Archive/PageViewer';
import Step1Pages from '../../Archive/Step1Pages';
import { PagesController } from '../../Archive/usePages';

// The "Pages" tab of "Modifier le document".
//
// With pages: the wizard's step 1 collector as-is (an "Ajouter
// une page" bar with file and camera, "N page(s) (max. 10)" counter, list with
// arrows and a trash can), with `minPages={1}` so that the document is not left
// without pages, and the QR state below. Each change is saved on the
// server instantly; the only thing that is not redone by itself is the PDF: "Régénérer le
// PDF" (in the modal's actions) assembles it anew, and in the meantime
// this tab shows the progress.
//
// Without pages (a single file, with no page rows behind it): the tab says so
// rather than offering a collector that would not apply.

type Props = {
  manager: PagesController;
  assembling: boolean;
  isLegacy: boolean;
};

const PagesTab: FC<Props> = ({ manager, assembling, isLegacy }) => {
  if (assembling) {
    return <AssemblingProgress pageCount={manager.pages.length} />;
  }

  if (isLegacy) {
    return (
      <Box
        data-testid="legacy-file"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 3,
          borderRadius: `${radii.card}px`,
          border: `2px dashed ${colors.accent}`,
        }}
      >
        <Typography sx={{ fontWeight: fontWeights.medium }}>
          Ce document a été archivé avec un fichier unique, sans pages.
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', opacity: 0.85 }}>
          Il ne peut pas être complété page par page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Step1Pages
        rows={manager.rows}
        busy={manager.busy}
        uploadsInProgress={manager.uploadsInProgress}
        minPages={1}
        onAddFiles={manager.addFiles}
        onMove={manager.movePage}
        onRemove={manager.removePage}
        onRetry={manager.retryUpload}
        onDismiss={manager.dismissUpload}
      />
      {manager.pages.length > 0 && (
        <QrStatus qr={manager.qr} pages={manager.pages} />
      )}
    </Box>
  );
};

export default PagesTab;
